(() => {
  "use strict";

  const registrations = new Map();
  const instances = new WeakMap();
  let domReady = document.readyState !== "loading";

  const rootsFor = (scope, selector) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(selector)) {
      roots.push(scope);
    }

    if (typeof scope.querySelectorAll === "function") {
      roots.push(...scope.querySelectorAll(selector));
    }

    return roots;
  };

  const initializeRegistration = (name, registration, scope = document) => {
    rootsFor(scope, registration.selector).forEach((root) => {
      let rootInstances = instances.get(root);

      if (!rootInstances) {
        rootInstances = new Map();
        instances.set(root, rootInstances);
      }

      if (rootInstances.has(name)) return;

      const cleanup = registration.initialize(root);
      rootInstances.set(name, typeof cleanup === "function" ? cleanup : null);
    });
  };

  const initialize = (scope = document) => {
    registrations.forEach((registration, name) => {
      initializeRegistration(name, registration, scope);
    });
  };

  const destroyRoot = (root, name) => {
    const rootInstances = instances.get(root);
    if (!rootInstances || !rootInstances.has(name)) return;

    const cleanup = rootInstances.get(name);

    if (cleanup) {
      try {
        cleanup();
      } catch (error) {
        console.error(`Reneora cleanup failed for "${name}".`, error);
      }
    }

    rootInstances.delete(name);

    if (rootInstances.size === 0) {
      instances.delete(root);
    }
  };

  const destroy = (scope) => {
    registrations.forEach((registration, name) => {
      rootsFor(scope, registration.selector).forEach((root) => {
        destroyRoot(root, name);
      });
    });
  };

  const register = (name, selector, initializeComponent) => {
    if (!name || !selector || typeof initializeComponent !== "function") {
      throw new TypeError("ReneoraTheme.register requires a name, selector, and initializer.");
    }

    if (registrations.has(name)) {
      return false;
    }

    const registration = { selector, initialize: initializeComponent };
    registrations.set(name, registration);

    if (domReady) {
      initializeRegistration(name, registration, document);
    }

    return true;
  };

  window.ReneoraTheme = Object.freeze({
    register,
    init: initialize,
    destroy
  });

  register('header-panels', '.reneora-header', (root) => {
    const panels = [...root.querySelectorAll('details')];
    const bag = root.querySelector('.reneora-header__bag');
    const count = bag?.querySelector('span');
    const syncBag = () => {
      if (!count) return;
      const value = Number(count.textContent.trim());
      if (Number.isInteger(value) && value >= 0) bag.setAttribute('aria-label', `Shopping bag, ${value} ${value === 1 ? 'item' : 'items'}`);
    };
    const bagObserver = count ? new MutationObserver(syncBag) : null;
    bagObserver?.observe(count, { childList: true, characterData: true, subtree: true });
    syncBag();
    const onToggle = (event) => {
      if (event.target.open) panels.forEach((panel) => {
        if (panel !== event.target && !panel.contains(event.target) && !event.target.contains(panel)) panel.open = false;
      });
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      const opened = [...panels].reverse().find((panel) => panel.open);
      if (opened) { opened.open = false; opened.querySelector('summary').focus(); }
    };
    const onOutside = (event) => {
      if (!root.contains(event.target)) panels.forEach((panel) => { panel.open = false; });
    };
    panels.forEach((panel) => panel.addEventListener('toggle', onToggle));
    root.addEventListener('keydown', onKey);
    document.addEventListener('click', onOutside);
    return () => {
      bagObserver?.disconnect();
      panels.forEach((panel) => panel.removeEventListener('toggle', onToggle));
      root.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onOutside);
    };
  });

  const onReady = () => {
    if (domReady) return;
    domReady = true;
    initialize(document);
  };

  if (domReady) {
    initialize(document);
  } else {
    document.addEventListener("DOMContentLoaded", onReady, { once: true });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });
})();

/* Reneora: stop offscreen work without hiding page content. */
(() => {
  'use strict';
  const watches = new Set();
  let pageActive = true;
  function watch(element, callback, threshold = 0.2) {
    let visible = false, previous;
    const update = () => {
      const active = visible && pageActive && !document.hidden && element.isConnected;
      if (active !== previous) { previous = active; callback(active); }
    };
    const entry = { update };
    watches.add(entry);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(([item]) => {
      visible = item.isIntersecting && item.intersectionRatio >= threshold;
      update();
    }, { threshold: [0, threshold] }) : null;
    observer?.observe(element);
    update(); // Never start timers before the first visibility observation.
    return () => { observer?.disconnect(); watches.delete(entry); };
  }
  window.ReneoraVisibility = { watch };
  document.addEventListener('visibilitychange', () => watches.forEach(item => item.update()));
  window.addEventListener('pagehide', () => { pageActive = false; watches.forEach(item => item.update()); });
  window.addEventListener('pageshow', () => { pageActive = true; watches.forEach(item => item.update()); });
  const initialize = scope => {
    scope.querySelectorAll('video').forEach(video => {
      if (video.dataset.rnVisibilityBound) return;
      video.dataset.rnVisibilityBound = 'true';
      // User-initiated videos never restart just because they re-enter view.
      video.autoplay = false;
      video.removeAttribute('autoplay');
      let visible = false;
      const stopWatching = watch(video, active => { visible = active; if (!active) video.pause(); }, 0.25);
      video.addEventListener('play', () => { if (!visible || document.hidden) video.pause(); });
      video.closest('.shopify-section')?.addEventListener('shopify:section:unload', stopWatching, { once: true });
    });
    // Pause only CSS loops; never remove/restart a completed entrance animation.
    scope.querySelectorAll('.rn-guide__slides, .rn-message-strip, [data-reneora-message-strip]').forEach(el => {
      if (el.dataset.rnAnimationWatch) return;
      el.dataset.rnAnimationWatch = 'true';
      watch(el, active => el.classList.toggle('rn-offscreen-paused', !active), 0.01);
    });
    // Keep native lazy loading during first paint; warm only nearby visible cards afterwards.
    if ('IntersectionObserver' in window) {
      const warmNearbyImages = () => {
        const preloader = new IntersectionObserver(entries => entries.forEach(entry => {
          const img = entry.target;
          if (!entry.isIntersecting || img.closest('[hidden], [aria-hidden="true"]')) return;
          img.loading = 'eager';
          preloader.unobserve(img);
        }), { rootMargin: '200px 0px', threshold: 0 });
        scope.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (!img.closest('[hidden], [aria-hidden="true"]')) preloader.observe(img);
        });
        scope.addEventListener?.('shopify:section:unload', () => preloader.disconnect(), { once: true });
      };
      if (document.readyState === 'complete') warmNearbyImages();
      else window.addEventListener('load', warmNearbyImages, { once: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();


/* Name only Google's merchant iframe; preserve provider-supplied titles. */
(() => {
  const selector = 'iframe#merchantwidgetiframe';
  const label = document.documentElement.lang.toLowerCase().startsWith('fr')
    ? 'Informations sur la boutique Reneora — Google'
    : 'Reneora store information — Google';
  const nameFrame = frame => {
    if (frame.matches(selector) && !frame.getAttribute('title')?.trim()) {
      frame.setAttribute('title', label);
    }
  };
  const inspect = node => {
    if (node.nodeType !== 1) return;
    nameFrame(node);
    node.querySelectorAll(selector).forEach(nameFrame);
  };
  const start = () => {
    inspect(document.body);
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') nameFrame(record.target);
        else record.addedNodes.forEach(inspect);
      }
    }).observe(document.body, {childList:true, subtree:true, attributes:true, attributeFilter:['id','title']});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
